import React, { useState, useCallback, useRef, useEffect, useMemo, KeyboardEvent } from 'react';

import { Button, Input } from '@ergeon/core-components';
import { XMarkIcon } from '@ergeon/icons/hero/micro';

import { MIN_SEARCH_LENGTH } from '../constants';
import { useEmailAutocomplete } from '../hooks/useEmailAutocomplete';
import { addEmailToString, getLastPartialEmail } from '../utils/emailRecipients';

import SuggestionsDropdown from './SuggestionsDropdown';

type EmailAutocompleteInputProps = {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  label?: string;
  disabled?: boolean;
  placeholder?: string;
};

const EmailAutocompleteInput: React.FC<EmailAutocompleteInputProps> = ({
  label,
  value,
  onChange,
  onClose,
  disabled,
  placeholder = 'Type to search...',
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentEmails = useMemo(
    () => value.split(',').map((e) => e.trim()).filter(Boolean),
    [value],
  );
  const { suggestions, search, clearSuggestions } = useEmailAutocomplete(currentEmails);

  const closeSuggestions = useCallback(() => {
    setShowSuggestions(false);
    setSelectedIndex(-1);
  }, []);

  const selectEmail = useCallback((email: string) => {
    const newValue = addEmailToString(value, email);
    if (newValue !== value) {
      onChange(newValue);
    }
    clearSuggestions();
    closeSuggestions();
  }, [value, onChange, clearSuggestions, closeSuggestions]);

  const handleInputChange = (newValue: string) => {
    onChange(newValue);
    setSelectedIndex(-1);

    const lastPart = getLastPartialEmail(newValue);
    if (lastPart.length >= MIN_SEARCH_LENGTH) {
      search(lastPart);
      setShowSuggestions(true);
    } else {
      clearSuggestions();
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (!suggestions.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          event.preventDefault();
          selectEmail(suggestions[selectedIndex].email);
        }
        break;
      case 'Escape':
        closeSuggestions();
        break;
    }
  }, [suggestions, selectedIndex, selectEmail, closeSuggestions]);

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="flex items-center gap-2" ref={containerRef}>
      <div className="flex-1 relative">
        <Input
          className="flex-1"
          disabled={disabled}
          label={label}
          onChange={(_event, _name, val) => handleInputChange(val || '')}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          value={value} />
        {showSuggestions && (
          <SuggestionsDropdown
            onSelect={selectEmail}
            selectedIndex={selectedIndex}
            suggestions={suggestions} />
        )}
      </div>
      <Button
        data-testid={`close-${label?.toLowerCase().replace(/\s+/g, '-') || 'field'}`}
        flavor="regular"
        isSquare
        onClick={() => {
          onChange('');
          onClose();
        }}
        size="large"
        taste="boundless">
        <XMarkIcon className="size-5" />
      </Button>
    </div>
  );
};

export default EmailAutocompleteInput;
